import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutData } from "@/mocks/articles";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d9nj0k4it35ii3jdlrt0";

export default function AboutPage() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于丁香花影院",
    description: aboutData.description,
    url: "https://www.lilaccinema.com/about",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: aboutData.brandName,
    url: "https://www.lilaccinema.com",
    description: aboutData.description,
    foundingDate: aboutData.founded,
    contactPoint: {
      "@type": "ContactPoint",
      email: aboutData.contact.email,
      contactType: "customer service",
      availableLanguage: ["Chinese"],
      hoursAvailable: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          opens: "09:00",
          closes: "22:00",
        },
      ],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "丁香花影院是什么？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "丁香花影院是国内领先的在线视频点播平台，提供海量高清电影电视剧免费在线观看服务，支持多种设备播放。",
        },
      },
      {
        "@type": "Question",
        name: "丁香花影院是否需要注册？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "丁香花影院无需注册即可免费观看所有影视内容，直接打开网站即可享受高清观影体验。",
        },
      },
      {
        "@type": "Question",
        name: "丁香花影院的画质怎么样？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "丁香花影院支持1080P至4K超高清画质播放，采用先进CDN加速技术，确保观影流畅不卡顿。",
        },
      },
    ],
  };

  return (
    <>
      <Seo
        title="关于我们 - 丁香花影院 | 品牌介绍与联系方式"
        description={`${aboutData.brandName}${aboutData.slogan}。${aboutData.description}海量影视资源，高清在线观看，尽在丁香花影院。`}
        keywords="丁香花影院,在线影院,关于丁香花影院,免费电影平台,在线视频点播"
        canonicalUrl="https://www.lilaccinema.com/about"
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20cinema%20brand%20about%20page%20warm%20elegant%20golden%20theater%20atmosphere&width=1200&height=630&seq=og-about&orientation=landscape"
        schemaJson={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8">关于丁香花影院</h1>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="text-lg font-semibold text-foreground-950 mb-4">品牌介绍</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {aboutData.description}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  自{aboutData.founded}年成立以来，丁香花影院始终秉持「让每个人都能轻松享受高品质影视娱乐」的初心，不断优化产品体验，丰富内容资源。经过多年的发展，我们已经积累了超过10,000部影视资源，服务了超过500万活跃用户。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutData.features.map((feature, idx) => (
                  <div key={idx} className="p-5 rounded-lg bg-background-100/70 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 mb-3">
                      <i className={`${feature.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-lg font-semibold text-foreground-950 mb-6">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {aboutData.testimonials.map((testimonial, idx) => (
                  <div key={idx} className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < testimonial.rating ? "text-accent-500 ri-star-fill" : "text-background-300 ri-star-line"}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      {testimonial.content}
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">— {testimonial.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-lg font-semibold text-foreground-950 mb-4">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">微信公众号</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">工作时间</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-foreground-950 mb-4">发送消息</h2>
                <form
                  id="about-contact-form"
                  data-readdy-form
                  action={CONTACT_FORM_URL}
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      alert("消息已发送，我们会尽快回复！");
                      form.reset();
                      return;
                    }
                    try {
                      const formData = new FormData(form);
                      const response = await fetch(form.action, {
                        method: "POST",
                        headers: { "Content-Type": "application/x-www-form-urlencoded" },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const responseText = await response.text();
                      let parsed;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      if (response.ok && parsed?.code === "OK") {
                        alert("消息已发送，我们会尽快回复！");
                        form.reset();
                      } else {
                        const errMsg = parsed?.meta?.message || parsed?.message || "发送失败，请稍后重试";
                        alert(errMsg);
                      }
                    } catch {
                      alert("发送失败，请检查网络后重试");
                    }
                  }}
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的消息（不超过500字）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors duration-200 resize-none"
                      required
                    ></textarea>
                  </div>
                  <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                    style={{ backgroundColor: "oklch(var(--accent-500))", color: "#fff" }}
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}